/**
 * POApproval.tsx — Token-based approver review screen  (Phase 65)
 *
 * Route: /po-approval?token=…[&action=APPROVE|DECLINE]
 *
 * No Entra login required — accessible by anyone with the magic link.
 * Mirrors the pattern of InvoicePortal.tsx (token-based review).
 *
 * Behaviour:
 *  1. On mount, reads ?token from URL and fetches PO via GET /mgmt/po/review?token=…
 *  2. Shows PO summary + line items + amounts
 *  3. Two buttons: Approve (green) | Decline (red, requires reason text)
 *  4. POSTs to /mgmt/po/review/action
 *  5. Shows success/error result screen
 */

import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getPOByToken, submitTokenAction } from '../api/poApi';
import {
  PO_STATUS_LABELS,
  PO_STATUS_COLORS,
  type PurchaseOrder,
  type POLineItem,
} from '../types/poTypes';

function fmtCurrency(n: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);
}

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'long', day: 'numeric', year: 'numeric',
  });
}

const POApproval: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token          = searchParams.get('token') || '';
  const defaultAction  = searchParams.get('action')?.toUpperCase() as 'APPROVE' | 'DECLINE' | null;

  const [po, setPo]             = useState<PurchaseOrder | null>(null);
  const [loading, setLoading]   = useState(true);
  const [tokenError, setTokenError] = useState<string | null>(null);

  const [view, setView]         = useState<'review' | 'decline-reason' | 'success' | 'error'>('review');
  const [declineReason, setDeclineReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [resultMessage, setResultMessage] = useState('');

  useEffect(() => {
    if (!token) {
      setTokenError('No approval token found in this link. Please check the email and try again.');
      setLoading(false);
      return;
    }

    getPOByToken(token)
      .then(data => {
        setPo(data);
        // If URL already has ?action=APPROVE, auto-show approval confirmation
        // (but don't auto-submit — require explicit button click)
        if (defaultAction === 'DECLINE') {
          setView('decline-reason');
        }
      })
      .catch(err => {
        const msg = err?.response?.data?.error || 'This approval link is invalid or has expired.';
        setTokenError(msg);
      })
      .finally(() => setLoading(false));
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleAction(action: 'APPROVE' | 'DECLINE') {
    if (action === 'DECLINE' && view !== 'decline-reason') {
      setView('decline-reason');
      return;
    }
    if (action === 'DECLINE' && !declineReason.trim()) {
      return; // button is disabled; just a guard
    }

    setSubmitting(true);
    try {
      const res = await submitTokenAction({
        token,
        action,
        reason: action === 'DECLINE' ? declineReason.trim() : undefined,
      });
      setResultMessage(
        action === 'APPROVE'
          ? `✓ Purchase Order ${po?.po_number} has been approved. New status: ${PO_STATUS_LABELS[res.status]}.`
          : `✗ Purchase Order ${po?.po_number} has been declined.`,
      );
      setView('success');
    } catch (err: any) {
      const msg = err?.response?.data?.error || 'An error occurred. Please try again or contact AP.';
      setResultMessage(msg);
      setView('error');
    } finally {
      setSubmitting(false);
    }
  }

  // ── Loading state ──────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen text-gray-400 text-sm">
        Loading purchase order…
      </div>
    );
  }

  // ── Token error ────────────────────────────────────────────────────────────
  if (tokenError) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="max-w-md w-full bg-white border border-red-200 rounded-xl shadow-sm p-8 text-center">
          <div className="text-4xl mb-4">⚠️</div>
          <h1 className="text-xl font-bold text-gray-900 mb-2">Link Unavailable</h1>
          <p className="text-gray-500 text-sm">{tokenError}</p>
          <p className="text-gray-400 text-xs mt-4">
            If you believe this is an error, please contact the AP team at{' '}
            <a href="mailto:ap@powerlinkonline.com" className="underline">
              ap@powerlinkonline.com
            </a>
            .
          </p>
        </div>
      </div>
    );
  }

  if (!po) return null;

  const lines: POLineItem[] = po.lines || [];

  // ── Success / Error result screen ──────────────────────────────────────────
  if (view === 'success' || view === 'error') {
    const isSuccess = view === 'success';
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className={`max-w-md w-full bg-white border rounded-xl shadow-sm p-8 text-center ${isSuccess ? 'border-green-200' : 'border-red-200'}`}>
          <div className="text-5xl mb-4">{isSuccess ? '✅' : '❌'}</div>
          <h1 className="text-xl font-bold text-gray-900 mb-3">
            {isSuccess ? 'Action Recorded' : 'Something Went Wrong'}
          </h1>
          <p className="text-gray-600 text-sm">{resultMessage}</p>
          {!isSuccess && (
            <button
              onClick={() => setView('review')}
              className="mt-6 text-sm text-indigo-600 underline"
            >
              Try again
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-2xl mx-auto">

        {/* Branding */}
        <div className="text-center mb-8">
          <div className="text-2xl font-bold text-indigo-700 tracking-tight">Powerlink AP</div>
          <div className="text-sm text-gray-400 mt-1">Purchase Order Review</div>
        </div>

        {/* PO summary card */}
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden mb-6">
          <div className="px-6 py-5 border-b border-gray-100 flex items-start justify-between">
            <div>
              <div className="text-xs text-gray-400 uppercase tracking-wide mb-1">Purchase Order</div>
              <div className="text-2xl font-bold text-gray-900 font-mono">{po.po_number}</div>
              <div className="text-sm text-gray-500 mt-1">
                Requested by {po.requested_by_name || po.requested_by_email} on {fmtDate(po.requested_at)}
              </div>
            </div>
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${PO_STATUS_COLORS[po.status]}`}>
              {PO_STATUS_LABELS[po.status]}
            </span>
          </div>

          <div className="px-6 py-4 grid grid-cols-2 gap-4 text-sm border-b border-gray-100">
            <div>
              <div className="text-gray-400 text-xs uppercase tracking-wide mb-1">Vendor</div>
              <div className="font-medium text-gray-800">{po.vendor_name}</div>
              {po.vendor_number && (
                <div className="text-gray-400 text-xs font-mono"># {po.vendor_number}</div>
              )}
            </div>
            <div>
              <div className="text-gray-400 text-xs uppercase tracking-wide mb-1">Job</div>
              <div className="font-medium text-gray-800">{po.job_number}</div>
              {po.job_name && <div className="text-gray-400 text-xs">{po.job_name}</div>}
            </div>
            {po.description && (
              <div className="col-span-2">
                <div className="text-gray-400 text-xs uppercase tracking-wide mb-1">Description</div>
                <div className="text-gray-700">{po.description}</div>
              </div>
            )}
          </div>

          {/* Line items */}
          <div className="px-6 py-4">
            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
              Line Items
            </div>
            <div className="space-y-2">
              {lines.map(line => (
                <div key={line.id} className="flex items-start justify-between text-sm">
                  <div className="flex-1 pr-4">
                    <div className="text-gray-800 font-medium">{line.description}</div>
                    <div className="text-gray-400 text-xs">
                      {line.gl_account && <span className="mr-3">GL {line.gl_account}</span>}
                      {line.job_number && po.job_number !== line.job_number && (
                        <span>Job {line.job_number}</span>
                      )}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-medium text-gray-900">{fmtCurrency(line.line_total)}</div>
                    <div className="text-gray-400 text-xs">
                      {line.quantity} × {fmtCurrency(line.unit_price)}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-4 pt-3 border-t border-gray-100 flex justify-end">
              <div className="text-right">
                <div className="text-xs text-gray-400 uppercase tracking-wide">Total Amount</div>
                <div className="text-2xl font-bold text-gray-900">{fmtCurrency(po.total_amount)}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Decline reason input */}
        {view === 'decline-reason' && (
          <div className="bg-white border border-red-200 rounded-xl shadow-sm p-6 mb-6">
            <h2 className="text-base font-semibold text-gray-800 mb-2">Reason for Declining</h2>
            <p className="text-sm text-gray-500 mb-3">
              Please provide a reason so the requestor knows what to change.
            </p>
            <textarea
              rows={3}
              value={declineReason}
              onChange={e => setDeclineReason(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-300 resize-none"
              placeholder="e.g. Budget not approved for this quarter. Please resubmit in Q3."
              autoFocus
            />
            <div className="flex gap-3 mt-3">
              <button
                onClick={() => setView('review')}
                className="px-4 py-2 text-sm border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                ← Back
              </button>
              <button
                onClick={() => handleAction('DECLINE')}
                disabled={submitting || !declineReason.trim()}
                className="flex-1 px-4 py-2 text-sm bg-red-600 text-white font-medium rounded-md hover:bg-red-700 disabled:opacity-50 transition"
              >
                {submitting ? 'Submitting…' : 'Confirm Decline'}
              </button>
            </div>
          </div>
        )}

        {/* Action buttons */}
        {view === 'review' && (
          <div className="grid grid-cols-2 gap-4">
            <button
              onClick={() => handleAction('DECLINE')}
              disabled={submitting}
              className="py-3 text-sm font-medium border-2 border-red-400 text-red-600 rounded-xl hover:bg-red-50 transition disabled:opacity-50"
            >
              ✗ Decline
            </button>
            <button
              onClick={() => handleAction('APPROVE')}
              disabled={submitting}
              className="py-3 text-sm font-medium bg-green-600 text-white rounded-xl hover:bg-green-700 transition disabled:opacity-50 shadow-sm"
            >
              {submitting ? 'Submitting…' : '✓ Approve'}
            </button>
          </div>
        )}

        <p className="text-center text-xs text-gray-400 mt-6">
          Powerlink AP Portal · This link is single-use and expires after 5 business days.
        </p>
      </div>
    </div>
  );
};

export default POApproval;
